import { Button, Flex, Modal, toast } from '@workwhile/ui'
import {
  ToggleGroup,
  ToggleGroupItem,
} from 'components/core/Toggle/ToggleGroup'
import { useSendTipsMutation } from 'queries/worker'
import { useState } from 'react'
import styled from 'styled-components'
import {
  BulkTipModalVariantCustom,
  BulkTipModalVariantTipEach,
  BulkTipModalVariantTotalTip,
} from './components'
import { BulkTipVariant } from './constants'
import { parseSendTipsResult } from './utils'

type Props = {
  shiftId: number
  isOpen: boolean
  onClose: () => void
}

const ToggleGroupVariant = styled(ToggleGroupItem)`
  padding: 4px;
  height: 40px;
`

const SendButton = ({
  variant,
  isLoading,
}: {
  variant: BulkTipVariant
  isLoading: boolean
}) => {
  return (
    <Button key={variant} type="submit" form={variant} loading={isLoading}>
      Send Tips
    </Button>
  )
}

export const BulkTipModal = ({ shiftId, isOpen, onClose }: Props) => {
  const [variant, setVariant] = useState<BulkTipVariant>(BulkTipVariant.Custom)

  const { mutate: sendTips, isPending: isSendingTips } = useSendTipsMutation(
    { shiftId },
    {
      onError: (error) => {
        toast.error(error.message)
        onClose()
      },
      onSuccess: (data) => {
        const { successes, errorMessages } = parseSendTipsResult(data)
        if (successes > 0) {
          toast.success(
            `${successes} tip${successes > 1 ? 's' : ''} sent successfully`
          )
        }

        errorMessages.forEach((message) => {
          /** Manually dismiss individual worker errors */
          toast.error(message, { autoClose: false })
        })

        onClose()
      },
    }
  )

  return (
    <Modal
      customCta={<SendButton variant={variant} isLoading={isSendingTips} />}
      onClose={onClose}
      open={isOpen}
      showCancel
      title="Tip Workers"
    >
      <Flex flexDirection="column" gap={3}>
        <ToggleGroup
          type="single"
          value={variant}
          onValueChange={(value: BulkTipVariant) => {
            setVariant(value)
          }}
          style={{ padding: 4 }}
        >
          <ToggleGroupVariant value={BulkTipVariant.TotalTip}>
            Total Tip
          </ToggleGroupVariant>
          <ToggleGroupVariant value={BulkTipVariant.TipEach}>
            Tip Each
          </ToggleGroupVariant>
          <ToggleGroupVariant value={BulkTipVariant.Custom}>
            Custom
          </ToggleGroupVariant>
        </ToggleGroup>

        {variant === BulkTipVariant.TotalTip && (
          <BulkTipModalVariantTotalTip shiftId={shiftId} onSubmit={sendTips} />
        )}
        {variant === BulkTipVariant.TipEach && (
          <BulkTipModalVariantTipEach shiftId={shiftId} onSubmit={sendTips} />
        )}
        {variant === BulkTipVariant.Custom && (
          <BulkTipModalVariantCustom shiftId={shiftId} onSubmit={sendTips} />
        )}
      </Flex>
    </Modal>
  )
}
